/**
 * Teltonika cellular antenna / LTE+5G router catalog.
 *
 * Teltonika dominates the industrial cellular CPE market — small DIN-
 * rail / aluminium-housing routers with external SMA antennas, dual SIM,
 * GPS, RS-232/RS-485 serial, and digital I/O. Covers the RUT series
 * (compact Cat 4 / Cat 6), RUTX series (enterprise Cat 6 / 5G), RUTM
 * series (US/Canada 5G), and the newer RUTC50 (5G + Wi-Fi 6).
 *
 * Categorisation rationale: same as the Mikrotik Chateau line — these
 * are devices whose PRIMARY function is cellular connectivity, even
 * the wired-only RUTX09 (LTE Cat 6 with no Wi-Fi). The pattern field
 * is 'omni' because Teltonika ships them with external whip antennas
 * on SMA connectors (omnidirectional by default; users can swap to
 * directional panel antennas separately).
 *
 * Every entry verified against the Teltonika product page +
 * authorised distributor datasheet quotes (Linovision, Wifi-Stock,
 * Mouser, Cisper, Grid Connect, WestwardSales — all of whom cite the
 * official datasheet PDF). The teltonika-networks.com product pages
 * return HTTP 404 to WebFetch in this run; specs below are mirrored
 * via distributor product pages that reproduce the datasheet verbatim.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const TELTONIKA_CELLULAR_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // RUT series — compact industrial Cat 4 / Cat 6 routers
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.teltonika-networks.com/products/routers/rut241
  // SRC: https://linovision.com/products/teltonika-rut241-industrial-4g-lte-cellular-wi-fi-router
  // SRC: https://westwardsales.com/teltonika-rut241-lte-router
  //      RUT241: LTE Cat 4 (150/50 Mbps), 2× Fast Ethernet 10/100, Wi-Fi
  //      802.11b/g/n (Wi-Fi 4), 1× SIM (with eSIM variant), 2× SMA LTE +
  //      1× RP-SMA Wi-Fi, 128 MB DDR2 RAM, <6.5 W max, -40..+75 °C,
  //      aluminium 83×25×74 mm.
  {
    id: 'teltonika-rut241',
    manufacturer: 'Teltonika',
    model: 'RUT241',
    category: 'cellular-antenna',
    tagline:
      'RUT241 — compact Cat 4 industrial 4G router, 2× FE, Wi-Fi 4, -40..+75 °C',
    verified: true,
    datasheetUrl: 'https://www.teltonika-networks.com/products/routers/rut241',
    specs: {
      technology: 'lte-cat4',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2, // typical SMA whip antenna gain shipped in box
      simSlots: 1, // 1 SIM with eSIM option on -eSIM variants
      ipRating: 'IP30', // aluminium housing, indoor / cabinet rating
      powerConsumptionW: 6.5,
    },
  },

  // SRC: https://www.teltonika-networks.com/products/routers/rut360
  // SRC: https://www.gridconnect.com/products/teltonika-rut360-lte-cat6-industrial-cellular-router
  // SRC: https://westwardsales.com/teltonika-rut360-lte-cat6-router
  //      RUT360: LTE Cat 6 (300/50 Mbps), 2× Fast Ethernet, Wi-Fi 4
  //      (802.11b/g/n), QCA9531 650 MHz, 128 MB DDR2, 16 MB flash,
  //      2× swivel SMA LTE antennas + 2× RP-SMA Wi-Fi antennas
  //      included, 18 W PSU. NOTE: marked DISCONTINUED on the product
  //      page (successor: RUT361). Retained for installs still
  //      using it as a spare-part reference.
  {
    id: 'teltonika-rut360',
    manufacturer: 'Teltonika',
    model: 'RUT360',
    category: 'cellular-antenna',
    tagline:
      'RUT360 — Cat 6 industrial 4G router, 2× FE, Wi-Fi 4, 300 Mbps (legacy)',
    verified: true,
    datasheetUrl: 'https://www.teltonika-networks.com/products/routers/rut360',
    specs: {
      technology: 'lte-cat6',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 1,
      ipRating: 'IP30',
    },
  },

  // SRC: https://www.teltonika-networks.com/products/routers/rut956
  // SRC: https://www.amazon.com/Teltonika-RUT956A00A00-Industrial-Compatible-Connectivity/dp/B0CK1TDSQC
  // SRC: https://linovision.com/products/teltonika-rut956-dual-sim-industrial-4g-lte-cat4-cellular-router
  //      RUT956: LTE Cat 4 (150/50 Mbps), 4× 10/100 Ethernet, dual
  //      Mini-SIM (2FF) 1.8/3 V + optional eSIM, Wi-Fi 4 (802.11b/g/n),
  //      1× DB9 RS-232 + 1× 6-pin RS-485 (full + half duplex), 2× DI +
  //      2× DO on 10-pin industrial socket + 1× DI/DO on 4-pin power,
  //      GNSS / GPS, 9-30 VDC, -40..+75 °C, aluminium 110×50×100 mm
  //      287 g DIN-rail mountable.
  {
    id: 'teltonika-rut956',
    manufacturer: 'Teltonika',
    model: 'RUT956',
    category: 'cellular-antenna',
    tagline:
      'RUT956 — Cat 4 industrial DIN, 4× FE, dual SIM, RS-232/485, GPS, DI/DO',
    verified: true,
    datasheetUrl: 'https://www.teltonika-networks.com/products/routers/rut956',
    specs: {
      technology: 'lte-cat4',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // RUTX series — enterprise Cat 6 + 5G
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.teltonika-networks.com/products/routers/rutx09
  // SRC: https://global.microless.com/product/teltonika-rutx09-lte-cat6-industrial-cellular-router-...
  // SRC: https://www.wifi-stock.com/details/teltonika-lte-a-cat6-cellular-iot-dual-sim-router-rutx09.html
  //      RUTX09: LTE-A Cat 6 with Carrier Aggregation, 300 Mbps DL, 4×
  //      Gigabit Ethernet (10/100/1000), dual SIM auto-failover, GNSS,
  //      USB 2.0, wired-only (no Wi-Fi), aluminium industrial casing.
  {
    id: 'teltonika-rutx09',
    manufacturer: 'Teltonika',
    model: 'RUTX09',
    category: 'cellular-antenna',
    tagline:
      'RUTX09 — Cat 6 wired enterprise, 4× GbE, dual SIM + GNSS, no Wi-Fi',
    verified: true,
    datasheetUrl: 'https://www.teltonika-networks.com/products/routers/rutx09',
    specs: {
      technology: 'lte-cat6',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
    },
  },

  // SRC: https://www.teltonika-networks.com/products/routers/rutx11
  // SRC: https://www.amazon.com/Teltonika-RUTX11-Industrial-Cellular-Router/dp/B0DC8F8TT8
  // SRC: https://linovision.com/products/teltonika-rutx11
  //      RUTX11: LTE Cat 6 (300 Mbps DL), 4× Gigabit Ethernet, Wi-Fi 5
  //      (802.11ac Wave 2 MU-MIMO, 867 Mbps), dual SIM auto-failover,
  //      Bluetooth LE, GPS / GNSS, RutOS, aluminium industrial casing.
  {
    id: 'teltonika-rutx11',
    manufacturer: 'Teltonika',
    model: 'RUTX11',
    category: 'cellular-antenna',
    tagline:
      'RUTX11 — Cat 6 enterprise, 4× GbE + Wi-Fi 5 dual-band, dual SIM, BLE + GPS',
    verified: true,
    datasheetUrl: 'https://www.teltonika-networks.com/products/routers/rutx11',
    specs: {
      technology: 'lte-cat6',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
    },
  },

  // SRC: https://www.teltonika-networks.com/products/routers/rutx50
  // SRC: https://www.sicetelecom.it/wp-content/uploads/2024/02/Teltonika-RUTX50-Datasheet-SICE-...
  // SRC: https://www.mouser.com/datasheet/2/1098/RUTX50_Datasheet_v1_1-3044139.pdf
  //      RUTX50: 5G Sub-6 SA/NSA, 2.1-3.3 Gbps DL (4×4 MIMO) / 900-600
  //      Mbps UL (2×2), LTE Cat 20 fallback. 5G NR EMEA NSA/SA bands:
  //      n1, n3, n5, n7, n8, n20, n28, n38, n40, n41, n77, n78. 5×
  //      Gigabit Ethernet, dual-band Wi-Fi 5 802.11ac 867 Mbps MU-MIMO,
  //      dual SIM. 9-50 VDC, max 18 W (<4 W idle), -40..+75 °C,
  //      aluminium 132×44.2×95.1 mm.
  {
    id: 'teltonika-rutx50',
    manufacturer: 'Teltonika',
    model: 'RUTX50',
    category: 'cellular-antenna',
    tagline:
      'RUTX50 — 5G Sub-6 SA/NSA, 3.3 Gbps DL, 4×4 MIMO, 5× GbE, dual SIM, Wi-Fi 5',
    verified: true,
    datasheetUrl: 'https://www.teltonika-networks.com/products/routers/rutx50',
    specs: {
      technology: '5g-sub6',
      bands:
        '5G NR SA/NSA EMEA: n1, n3, n5, n7, n8, n20, n28, n38, n40, n41, n77, n78; LTE Cat 20 fallback',
      mimo: '4×4',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
      powerConsumptionW: 18,
    },
  },

  // SRC: https://www.teltonika-networks.com/products/routers/rutm50
  // SRC: https://www.gridconnect.com/products/teltonika-rutm50-cellular-5g-router
  // SRC: https://networkdevicesinc.com/products/rutm50000000
  //      RUTM50: 5G SA/NSA Sub-6, FCC-certified US/Canada market, 3.4
  //      Gbps DL / 550 Mbps UL (4×4 MIMO), LTE Cat 19/18 fallback. 5G NR
  //      NSA bands: n2, n5, n7, n12, n13, n14, n25, n26, n29, n30, n38,
  //      n41, n48, n66, n70, n71, n77, n78. LTE-FDD bands: B2, B4, B5,
  //      B7, B12, B13, B14, B17, B25, B26, B29, B30, B66, B71. 5×
  //      Gigabit Ethernet, dual-band Wi-Fi 5 MU-MIMO (up to 150
  //      clients, mesh-capable), dual SIM auto-failover.
  {
    id: 'teltonika-rutm50',
    manufacturer: 'Teltonika',
    model: 'RUTM50',
    category: 'cellular-antenna',
    tagline:
      'RUTM50 — US/CA 5G Sub-6, 3.4 Gbps DL, 4×4 MIMO, 5× GbE, dual SIM, mesh Wi-Fi 5',
    verified: true,
    datasheetUrl: 'https://www.teltonika-networks.com/products/routers/rutm50',
    specs: {
      technology: '5g-sub6',
      bands:
        '5G NR NSA: n2, n5, n7, n12, n13, n14, n25, n26, n29, n30, n38, n41, n48, n66, n70, n71, n77, n78; LTE-FDD: B2, B4, B5, B7, B12, B13, B14, B17, B25, B26, B29, B30, B66, B71',
      mimo: '4×4',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
    },
  },

  // SRC: https://www.teltonika-networks.com/products/routers/rutc50
  // SRC: https://www.broadbandbuyer.com/products/52158-teltonika-rutc50-200200/
  // SRC: https://www.wifi-stock.com/details/teltonika-5g-4g-lte-cat-20-wi-fi-6-industrial-cellular-router-rutc50.html
  //      RUTC50: 5G Sub-6 SA/NSA, 3.4 Gbps DL (4×4 MIMO) / 900 Mbps UL
  //      (2×2), LTE Cat 20 fallback 2.0 Gbps DL / 210 Mbps UL. LTE
  //      bands: 1, 3, 5, 7, 8, 20, 28, 32. Wi-Fi 6 (802.11ax) dual-band
  //      MU-MIMO: 2402 Mbps on 5 GHz + 576 Mbps on 2.4 GHz. 5× Gigabit
  //      RJ45 (1 WAN + 4 LAN), 4× SMA mobile + 3× RP-SMA Wi-Fi + 1× SMA
  //      GNSS, dual SIM with auto-failover.
  {
    id: 'teltonika-rutc50',
    manufacturer: 'Teltonika',
    model: 'RUTC50',
    category: 'cellular-antenna',
    tagline:
      'RUTC50 — 5G Sub-6 + Wi-Fi 6 AX2978, Cat 20 fallback, 4×4 MIMO, 5× GbE',
    verified: true,
    datasheetUrl: 'https://www.teltonika-networks.com/products/routers/rutc50',
    specs: {
      technology: '5g-sub6',
      bands:
        '5G NR Sub-6 SA/NSA; LTE Cat 20 fallback bands: 1, 3, 5, 7, 8, 20, 28, 32',
      mimo: '4×4',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
    },
  },
];
